import DesignTools, { DesignToolsRef } from '#base/Designer/DesignTools.js';
import React, { forwardRef, memo, useImperativeHandle, useRef } from 'react';

export interface IProps {
  typeId: number;
  selectedFace;
  assetLibrary;
  assetGroups;
  assetGroupFilter: any;
  config;
  uploaded;
  uploadUrl;
  onUpload;
  allFacesElements: any;
  elements;
  selectedElements;
  hasElements: boolean;
  hasPremium: string;
  restrictionInfo: object;
  ItemInfoComponent;
  getTotalPrice: () => string;
  itemInfoProps;
  setView: (view: string) => void;
  toolView: string;
  setToolView: (view: string) => void;
  withText: boolean;
  withGroup: boolean;
  withJersey: boolean;
  withMonogram: boolean;
  withArtwork: boolean;
  withUpload: boolean;
  wantUploadsDeferred: boolean;
  graphicType: string;
  showReview: boolean;
  uploadFee: { rate: string; usualRate: string } | null;
  debug: boolean;
  mode?: string;
  units?: string;
}

export type ForwardedRefs = NonNullable<unknown> & DesignToolsRef;

const DesignerView: React.ForwardRefRenderFunction<ForwardedRefs, IProps> = (
  {
    assetGroups,
    assetGroupFilter,
    assetLibrary,
    config,
    allFacesElements,
    elements,
    hasElements,
    hasPremium,
    restrictionInfo,
    ItemInfoComponent,
    itemInfoProps,
    selectedElements,
    selectedFace,
    setToolView,
    toolView,
    uploaded,
    uploadUrl,
    onUpload,
    withText,
    withGroup,
    withJersey,
    withMonogram,
    withArtwork,
    withUpload,
    wantUploadsDeferred,
    graphicType,
    showReview = true,
    uploadFee,
    typeId,
    debug,
    mode,
    units,
  },
  forwardedRef
) => {
  const designToolsRef = useRef<DesignToolsRef>(null);

  useImperativeHandle(forwardedRef, () => ({
    undo: designToolsRef.current.undo,
  }));

  return (
    <div>
      <DesignTools
        ref={designToolsRef}
        view={toolView}
        setView={setToolView}
        {...{
          assetLibrary,
          assetGroups,
          assetGroupFilter,
          config,
          uploaded,
          uploadUrl,
          onUpload,
          allFacesElements,
          elements,
          selectedFace,
          selectedElements,
          withText,
          withGroup,
          withJersey,
          withMonogram,
          withArtwork,
          withUpload,
          wantUploadsDeferred,
          hasPremium,
          hasElements,
          restrictionInfo,
          graphicType,
          uploadFee,
          typeId,
          debug,
          mode,
          units,
        }}
      />

      {!toolView && (!selectedElements || selectedElements.length == 0) && (
        <div className='t-mt-4'>
          {hasPremium === 'face' && (
            <div className='t-mb-4 t-border-b t-border-gray-300 t-pb-4 t-text-left t-text-brand-red'>
              Premium assets cannot be used alongside other designs and text.
            </div>
          )}

          {ItemInfoComponent && <ItemInfoComponent {...{ ...itemInfoProps, showReview }} />}
        </div>
      )}
    </div>
  );
};

const ForwardedDesignerView = forwardRef(DesignerView);
export type DesignerViewRef = React.ElementRef<typeof ForwardedDesignerView>;

export default memo(ForwardedDesignerView);
